/**
 * FeedbackDashboard.jsx — admin-only stats view for site feedback.
 *
 * Route: /admin/feedback
 *
 * Auth: admin token entered once (stored in localStorage). Token is
 * sent as `x-admin-token` header. Server validates against
 * SITE_FEEDBACK_ADMIN_TOKEN env var. 401 clears the token and falls
 * back to the prompt.
 *
 * Renders:
 *   - Total responses + last-7-days count
 *   - Classic NPS score (with promoter/passive/detractor breakdown)
 *   - NPS distribution bar chart (0-10)
 *   - Sean Ellis PMF percentage + breakdown
 *   - Recent comments
 *
 * Built dependency-free with CSS bars — no chart library to keep the
 * bundle slim. Plenty good for the screenshots we want for interviews.
 *
 * Created 2026-05-03 (Sunday polish round 2).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import LogoIcon from '../components/Brand/LogoIcon';
import {
  fetchFeedbackStats,
  setAdminToken,
  getAdminToken,
  clearAdminToken
} from '../services/feedbackDashboardApi';
import './FeedbackDashboard.css';

// Human labels for the four PMF response codes.
const PMF_LABELS = {
  very_disappointed:     'Very disappointed',
  somewhat_disappointed: 'Somewhat disappointed',
  not_disappointed:      'Not disappointed',
  havent_tried:          "Haven't tried"
};

// PMF threshold: 40%+ "very disappointed" = product/market fit (Sean Ellis).
const PMF_FIT_THRESHOLD = 40;

// NPS bands for the bar-chart coloring + breakdown labels.
const npsBand = (score) => {
  if (score >= 9) return 'promoter';
  if (score >= 7) return 'passive';
  return 'detractor';
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return '—';
  }
};

const FeedbackDashboard = () => {
  const [tokenInput, setTokenInput] = useState('');
  const [authed, setAuthed] = useState(!!getAdminToken());

  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await fetchFeedbackStats();
    setLoading(false);
    if (res.success) {
      setStats(res.data);
    } else {
      setError(res.error || 'Failed to load.');
      if (res.unauthorized) {
        setAuthed(false);
        setStats(null);
      }
    }
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const handleTokenSubmit = (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    setAdminToken(tokenInput.trim());
    setTokenInput('');
    setAuthed(true);
  };

  const handleSignOut = () => {
    clearAdminToken();
    setAuthed(false);
    setStats(null);
  };

  // Compute the max bar value so the chart can scale relatively.
  const npsMax = stats
    ? Math.max(1, ...Object.values(stats.npsDistribution || {}))
    : 1;
  const pmfMax = stats
    ? Math.max(1, ...Object.values(stats.pmfDistribution || {}))
    : 1;

  return (
    <div className="fd-page">
      {/* ─── Pill nav ───────────────────────────────────────────── */}
      <header className="fd-nav-wrap">
        <nav className="fd-nav">
          <Link to="/" className="fd-logo">
            <LogoIcon size={26} />
            <span className="fd-logo-text">
              LocalGenie<span className="fd-logo-accent">.ai</span>
              <span className="fd-logo-admin"> · admin</span>
            </span>
          </Link>
          <div className="fd-nav-actions">
            {authed && (
              <button className="fd-nav-link" onClick={load} disabled={loading}>
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            )}
            {authed && (
              <button className="fd-nav-link" onClick={handleSignOut}>
                Sign out
              </button>
            )}
            <Link to="/" className="fd-nav-cta">
              Back to home →
            </Link>
          </div>
        </nav>
      </header>

      <main className="fd-main">
        {/* ─── Token gate ───────────────────────────────────────── */}
        {!authed && (
          <form className="fd-card fd-token-card" onSubmit={handleTokenSubmit}>
            <span className="fd-eyebrow">Restricted</span>
            <h1>Admin access required</h1>
            <p className="fd-token-help">
              Enter the admin token (matches <code>SITE_FEEDBACK_ADMIN_TOKEN</code>{' '}
              on the server). The token is stored in your browser's localStorage;
              sign out clears it.
            </p>
            <input
              type="password"
              className="fd-input"
              placeholder="Admin token"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              autoFocus
            />
            <button
              type="submit"
              className="fd-btn-primary"
              disabled={!tokenInput.trim()}
            >
              Unlock dashboard
            </button>
          </form>
        )}

        {/* ─── Authed view ──────────────────────────────────────── */}
        {authed && (
          <>
            {error && (
              <div className="fd-error" role="alert">
                {error}
              </div>
            )}

            {loading && !stats && (
              <div className="fd-card fd-loading">Loading stats...</div>
            )}

            {stats && (
              <>
                {/* ─── Top KPI strip ────────────────────────────── */}
                <div className="fd-kpis">
                  <div className="fd-kpi">
                    <span className="fd-kpi-label">Total responses</span>
                    <span className="fd-kpi-value">{stats.totalCount}</span>
                  </div>
                  <div className="fd-kpi">
                    <span className="fd-kpi-label">Last 7 days</span>
                    <span className="fd-kpi-value">{stats.last7DaysCount}</span>
                  </div>
                  <div className="fd-kpi">
                    <span className="fd-kpi-label">NPS score</span>
                    <span
                      className={
                        'fd-kpi-value ' +
                        (stats.npsScore == null
                          ? ''
                          : stats.npsScore >= 50
                          ? 'fd-kpi-good'
                          : stats.npsScore >= 0
                          ? 'fd-kpi-ok'
                          : 'fd-kpi-bad')
                      }
                    >
                      {stats.npsScore == null ? '—' : stats.npsScore}
                    </span>
                    <span className="fd-kpi-sub">
                      {stats.npsBreakdown
                        ? `${stats.npsBreakdown.promoters}P / ${stats.npsBreakdown.passives} Pa / ${stats.npsBreakdown.detractors}D`
                        : ''}
                    </span>
                  </div>
                  <div className="fd-kpi">
                    <span className="fd-kpi-label">PMF "very disappointed"</span>
                    <span
                      className={
                        'fd-kpi-value ' +
                        (stats.pmfPercent == null
                          ? ''
                          : stats.pmfPercent >= PMF_FIT_THRESHOLD
                          ? 'fd-kpi-good'
                          : 'fd-kpi-ok')
                      }
                    >
                      {stats.pmfPercent == null ? '—' : `${stats.pmfPercent}%`}
                    </span>
                    <span className="fd-kpi-sub">
                      {stats.pmfPercent != null && stats.pmfPercent >= PMF_FIT_THRESHOLD
                        ? '≥ 40% threshold met'
                        : 'Below 40% Sean Ellis threshold'}
                    </span>
                  </div>
                </div>

                {/* ─── NPS distribution bar chart ───────────────── */}
                <section className="fd-card">
                  <div className="fd-card-head">
                    <span className="fd-eyebrow">NPS distribution</span>
                    <h2>How likely to recommend</h2>
                  </div>
                  <div className="fd-bars-row">
                    {Object.entries(stats.npsDistribution || {}).map(([k, v]) => {
                      const score = Number(k);
                      const band = npsBand(score);
                      const heightPct = (v / npsMax) * 100;
                      return (
                        <div key={k} className="fd-bar-col">
                          <div className="fd-bar-track">
                            <div
                              className={`fd-bar fd-bar-${band}`}
                              style={{ height: `${heightPct}%` }}
                              title={`${v} response${v === 1 ? '' : 's'}`}
                            >
                              {v > 0 && <span className="fd-bar-count">{v}</span>}
                            </div>
                          </div>
                          <span className="fd-bar-label">{score}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="fd-bars-anchors">
                    <span>0 — Not at all likely</span>
                    <span>10 — Extremely likely</span>
                  </div>
                </section>

                {/* ─── PMF breakdown ────────────────────────────── */}
                <section className="fd-card">
                  <div className="fd-card-head">
                    <span className="fd-eyebrow">Sean Ellis PMF</span>
                    <h2>"How would you feel if LocalGenie went away?"</h2>
                  </div>
                  <div className="fd-pmf-list">
                    {Object.entries(stats.pmfDistribution || {}).map(([k, v]) => {
                      const widthPct = (v / pmfMax) * 100;
                      return (
                        <div key={k} className="fd-pmf-row">
                          <span className="fd-pmf-label">{PMF_LABELS[k] || k}</span>
                          <div className="fd-pmf-track">
                            <div
                              className={`fd-pmf-fill fd-pmf-${k}`}
                              style={{ width: `${widthPct}%` }}
                            />
                          </div>
                          <span className="fd-pmf-count">{v}</span>
                        </div>
                      );
                    })}
                  </div>
                </section>

                {/* ─── Recent comments ──────────────────────────── */}
                <section className="fd-card">
                  <div className="fd-card-head">
                    <span className="fd-eyebrow">Recent comments</span>
                    <h2>What people are saying</h2>
                  </div>
                  {(!stats.recentComments || stats.recentComments.length === 0) ? (
                    <p className="fd-empty">
                      No comments yet — every comment appears here as soon as
                      someone writes one.
                    </p>
                  ) : (
                    <ul className="fd-comments">
                      {stats.recentComments.map((c, i) => (
                        <li key={i} className="fd-comment">
                          <div className="fd-comment-meta">
                            <span className={`fd-nps-pill fd-nps-pill-${npsBand(c.npsScore)}`}>
                              NPS {c.npsScore}
                            </span>
                            {c.pmfResponse && (
                              <span className="fd-pmf-pill">
                                {PMF_LABELS[c.pmfResponse]}
                              </span>
                            )}
                            <span className="fd-comment-date">
                              {formatDate(c.createdAt)}
                            </span>
                          </div>
                          <p className="fd-comment-text">{c.comment}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default FeedbackDashboard;
